<template>
  <card card-body-classes="table-full-width">
    <template slot="header">
      <h4 class="card-title">{{ $t('topUp.tableTitle') }}
        <small v-if="filterDetails"
               class="ml-2 text-muted">{{ $t('topUp.tableFilterLabel', tableFilterLabelDetails) }}</small>
      </h4>
    </template>
    <div>
      <div
        class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
      >
        <el-select
          class="select-primary mb-3 pagination-select"
          v-model="pagination.perPage"
          placeholder="Per page"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <div class="row">
          <div class="col-6">
            <base-input>
              <el-input
                type="search"
                class="mb-3 search-input"
                prefix-icon="el-icon-search"
                :placeholder="$t('generic.searchInTablePlaceholder')"
                v-model="searchQuery"
                aria-controls="datatables"
              >
              </el-input>
            </base-input>
          </div>

        </div>
      </div>

      <el-table :data="queriedData" :row-class-name="tableRowClassName" :empty-text="$t('generic.noData')">
        <el-table-column type="expand">
          <template slot-scope="props">
            <top-up-details :details="props.row"/>
          </template>
        </el-table-column>
        <el-table-column
          v-for="column in tableColumns"
          :key="column.label"
          :min-width="column.minWidth"
          :width="column.width"
          :prop="column.prop"
          :label="$t(column.label)"
          :formatter="column.formatter"
        >
        </el-table-column>
        <el-table-column align="right" width="50">
          <div slot-scope="{ row }">
            <router-link :to="accountTopUpLinkUrl(row.account_id, row.id)">
              <i class="tim-icons icon-double-right"></i>
            </router-link>
          </div>
        </el-table-column>
      </el-table>
    </div>
    <div
      slot="footer"
      class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
      <div class="">
        <p class="card-category">
          {{ $t('generic.tablePaginationSummary', {from: from + 1, to: to, total: total}) }}
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      >
      </base-pagination>
    </div>
  </card>
</template>
<script>
import {Option, Select, Table, TableColumn} from 'element-ui';
import {BaseAlert, BasePagination} from 'src/components';
import Fuse from 'fuse.js';
import TopUpDetails from './TopUpDetails'
import linkUtils from "@/util/link_utils"

export default {
  name: 'top-ups-table',
  components: {
    BasePagination,
    BaseAlert,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    TopUpDetails
  },
  props: {
    details: {
      type: Array,
      required: true,
    },
    filterDetails: {
      type: Object,
      required: false,
    },
  },
  methods: {
    accountTopUpLinkUrl(id, top_up_id) {
      return linkUtils.accountTopUpPath(this.$router, {account_id: id, top_up_id: top_up_id});
    },
    tableRowClassName({row}) {
      if (row["status"] === "failed" || row["status"] === "initiation_failed") {
        return 'bg-warning';
      }
      return '';
    },
  },
  computed: {
    tableFilterLabelDetails: function () {
      let amount = "*"
      if (this.filterDetails.amount >= 0) {
        amount = this.$options.filters.money(this.filterDetails.amount)
      }
      return {
        date: this.$options.filters.dateOnly(this.filterDetails.date),
        amount: amount,
        merchantReference: this.filterDetails.merchantReference
      }
    },
    fuseSearch() {
      return new Fuse(this.details, {
        keys: this.propsToSearch,
        threshold: 0.3
      })
    },
    /***
     * Returns a page from the searched data or the whole data. Search is performed in the watch section below
     */
    queriedData() {
      let result = this.details;
      if (this.searchedData.length > 0) {
        result = this.searchedData;
      }
      return result.slice(this.from, this.to);
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchedData.length > 0
        ? this.searchedData.length
        : this.details.length;
    }
  },
  data() {
    return {
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0
      },
      apiFilter: [],
      searchQuery: '',
      propsToSearch: ['card_pan_suffix', 'merchant_reference'],
      tableColumns: [
        {
          prop: 'created_at',
          label: 'generic.created',
          formatter: (row, column, cellValue, index) => {
            return this.$options.filters.time(cellValue)
          },
          width: 200,
        },
        {
          prop: 'type',
          label: 'generic.type',
          width: 200,
        },
        {
          prop: 'card_pan_suffix',
          label: 'topUp.details.card_pan_suffix',
          width: 200,
        },
        {
          prop: 'merchant_reference',
          label: 'topUp.details.merchant_reference',
        },
        {
          prop: 'amount',
          label: 'topUp.details.amount',
          formatter: (row, column, cellValue, index) => {
            return this.$options.filters.price(cellValue)
          },
          width: 100,
        },
        {
          prop: 'status',
          label: 'generic.status',
          width: 100,
        },
        {
          prop: 'payment_reference',
          label: 'topUp.details.payment_reference',
        },
      ],
      searchedData: [],
    };
  },
  watch: {
    /**
     * Searches through the table data by a given query.
     * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
     * @param value of the query
     */
    searchQuery(value) {
      let result = this.details;
      if (value !== '') {
        result = this.fuseSearch.search(this.searchQuery);
      }
      this.searchedData = result;
    },
  }
};
</script>
<style scoped>
</style>
